/**
 * HomePage — Versão 3B do protótipo design/prototipos/dashboard-mockup (aprovada pelo Marcelo
 * em 14/10/2026), para todas as contas: a faixa escura ocupa a janela, com o resumo do que a
 * pessoa já capturou, os totais por tipo e o selo da licença à esquerda, o globo com as cidades
 * das listas de negócios locais à direita e as opções de captura no pé.
 *
 * As ferramentas ficam no menu do cabeçalho e o painel do embaixador, em Suporte > Programa de
 * recomendação (/referral). A barra de licença do rodapé não aparece aqui (AppShell).
 *
 * Movimento (23/10/2026): na primeira visita da sessão, os totais contam de 0 até os números da
 * pessoa (contarAte, 700 ms); depois, entram com .ds-entra (200 ms). Os cartões de captura são
 * cartões de entrada: sombra do nível 2 ao 3 (.ds-elevavel) e seta que avança 3 px
 * (.ds-seta-avanca, M10 b). A luz da licença fica em LicensePill (.ds-luz, M3 a).
 */
import {
  Suspense,
  lazy,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useMe } from '../api/useMe';
import { useSessionStore } from '../stores/sessionStore';
import { useCaptureStore } from '../stores/captureStore';
import { formatNumber } from '../lib/format';
import { contarAte } from '../lib/movimento';
import { RichText } from '../components/ui/RichText';
import { PRIMARY_CAPTURE_PRESENTATIONS } from '../config/capturePresentation';
import { HOME_MODULES, type HomeModule, type RegionSummary } from '../features/home/homeData';
import { useHomeDashboard } from '../features/home/useHomeDashboard';
import { cityPoints, regionPoints } from '../features/home/mapPoints';
import { LicensePill } from '../features/home/LicensePill';
import { marcarTotaisContados, totaisJaContados } from '../features/home/contagemTotais';
import { countText, coverageText, localeOf, type Translate } from '../features/home/homeText';
import { LoadingStatus, Skeleton } from '../components/common/Skeleton';
import { useGuiaStore } from '../features/guia/guiaStore';
import { comecarGuia } from '../features/guia/guiaAcoes';
import { useGuiaText, type GuiaKey } from '../features/guia/guiaText';
import '../features/home/home.css';
import { captureTypes } from '../lib/entitlements';

const GlobeMap = lazy(() => import('../features/home/GlobeMap').then((m) => ({ default: m.GlobeMap })));

/** Centro da esfera: o Brasil fica ~9° acima dele, na altura do texto. */
const BRAZIL_CENTER: [number, number] = [-49, -35];
const LAT_OFFSET = 9;
/** O contêiner do globo tem 122% da altura da faixa: o centro da esfera fica a 61% dela. */
const CONTAINER_HEIGHT = 1.22;
/** Raio visível da esfera: 60% da altura da faixa, no máximo 430 px (para não invadir o texto). */
const RADIUS_SHARE = 0.6;
const RADIUS_MAX = 430;
/** No mergulho, a região fica a 42% da altura da faixa. */
const DIVE_AT = 0.42;
/** Quanto os cartões de captura sobem sobre a faixa (px). */
const CARDS_OVERLAP = 80;

const MODULE_ICON: Record<HomeModule, string> = { places: 'storefront', phones: 'call', emails: 'mail' };

const UNIT_DEFAULTS: Record<HomeModule, string> = {
  places: 'negócios locais',
  phones: 'telefones',
  emails: 'emails',
};

const CAPTURE_DEFAULTS: Record<HomeModule, { title: string; details: string }> = {
  places: {
    title: 'Telefones de negócios locais',
    details: 'Telefone, site, redes e endereço das empresas no <b>Google Maps</b>.',
  },
  phones: {
    title: 'Telefones de empresas ou profissionais',
    details: 'Números encontrados em <b>sites e redes sociais</b>.',
  },
  emails: {
    title: 'Emails de empresas ou profissionais',
    details: 'Endereços encontrados em <b>sites e redes sociais</b>.',
  },
};

/**
 * Zoom que dá à esfera o raio visível pedido. A câmera da MapLibre é em perspectiva (distância
 * focal = 1,5 × altura do contêiner, no campo de visão padrão), então o raio real é maior que o
 * visível: R = A × (A + √(A² + f²)) ÷ f. E R = 512 × 2^zoom ÷ 2π ÷ cos(latitude do centro).
 */
function zoomForRadius(visible: number, containerHeight: number, lat: number): number {
  const f = 1.5 * containerHeight;
  const radius = (visible * (visible + Math.sqrt(visible * visible + f * f))) / f;
  return Math.log2((radius * 2 * Math.PI * Math.cos((lat * Math.PI) / 180)) / 512);
}

/**
 * Total de um tipo na faixa (só números da pessoa). Com `count`, conta do número na tela (0 na
 * primeira vez) até o atual, em 700 ms com a curva de entrada; se o total trocar no meio (totais
 * do /me → das listas), segue do que está na tela até o novo. Enquanto conta, o número fica fora
 * do leitor de tela (aria-hidden) e volta quando a tela mostra o número final: ele ouve só o
 * final, mesmo com a aba escondida (a contagem para junto com os quadros). Sem `count`, o número
 * entra com .ds-entra e de novo quando troca. Sem movimento, contarAte põe o número final na hora.
 */
function TileValue({
  value,
  locale,
  count,
}: {
  value: number;
  locale: string;
  count: boolean;
}): JSX.Element {
  const ref = useRef<HTMLElement>(null);
  /** O número na tela agora, de onde a próxima contagem parte. */
  const shown = useRef(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!count || !el) return undefined;
    const format = (n: number): string => {
      shown.current = n;
      // Chegou ao número final: o leitor de tela volta a ouvir o total (e ouve só ele).
      if (n === value) el.removeAttribute('aria-hidden');
      return formatNumber(n, locale);
    };
    el.setAttribute('aria-hidden', 'true');
    // O React já escreveu o número final: antes de pintar, volta ao da tela e conta dali.
    el.textContent = format(shown.current);
    const counting = contarAte(el, shown.current, value, format);
    if (!counting) {
      el.removeAttribute('aria-hidden');
      return undefined;
    }
    return () => {
      counting.stop();
      el.removeAttribute('aria-hidden');
    };
  }, [count, value, locale]);

  if (!count) {
    // A chave troca com o número: ele entra de novo quando o total muda.
    return (
      <strong key={value} className="vx-tile__value ds-entra">
        {formatNumber(value, locale)}
      </strong>
    );
  }
  return (
    <strong ref={ref} className="vx-tile__value">
      {formatNumber(value, locale)}
    </strong>
  );
}

/** Altura da faixa, atualizada quando a janela muda de tamanho (com uma pausa, para o globo não ser recriado a cada pixel). */
function useHeight(ref: RefObject<HTMLElement>): number {
  const [height, setHeight] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let timer = 0;
    const measure = (): void => setHeight(Math.round(el.getBoundingClientRect().height));
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      window.clearTimeout(timer);
      timer = window.setTimeout(measure, 250);
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
    };
  }, [ref]);
  return height;
}

/**
 * Centro da esfera: o do Brasil, calibrado pela imagem; se a região com mais negócios fica fora
 * do Brasil, a esfera abre virada para ela.
 */
function globeCenter(regions: RegionSummary[]): [number, number] {
  const main = regions[0];
  if (!main || main.key.startsWith('br:')) return BRAZIL_CENTER;
  return [main.lng, Math.max(-60, Math.min(60, main.lat - LAT_OFFSET))];
}

export function HomePage(): JSX.Element {
  const { t, i18n } = useTranslation();
  // A Home continua inscrita no /me (antes os totais dele iam para o registro de uso).
  useMe();
  const session = useSessionStore((s) => s.session);
  const openCaptureWindow = useCaptureStore((s) => s.openCaptureWindow);
  const data = useHomeDashboard();
  const { tx: gtx } = useGuiaText();
  const guiaLigado = useGuiaStore((g) => g.status === 'ativo');
  const heroRef = useRef<HTMLElement>(null);
  const heroHeight = useHeight(heroRef);
  // Movimento: os totais contam só na primeira visita da sessão (marcada quando as listas chegam,
  // com os números finais); a frase entra com fade só quando substitui as linhas de carregamento.
  const [countTotals] = useState(() => !totaisJaContados());
  const [summaryWaited] = useState(() => data.totalLists === null);
  useEffect(() => {
    if (data.totalLists !== null) marcarTotaisContados();
  }, [data.totalLists]);
  const summaryClass = summaryWaited ? 'ds-aparece' : undefined;

  // Números, datas e plurais seguem o idioma da tela (o mesmo dos textos).
  const lang = (i18n?.language ?? session?.language ?? 'pt').slice(0, 2);

  // Fundo escuro acima da página (rolagem elástica) e conteúdo claro só enquanto a Home está aberta.
  useEffect(() => {
    document.documentElement.classList.add('home-3b');
    return () => document.documentElement.classList.remove('home-3b');
  }, []);
  // Início sem listas (B2): sem os cartões de baixo, a página inteira fica escura.
  useEffect(() => {
    document.documentElement.classList.toggle('home-b2', data.empty);
    return () => document.documentElement.classList.remove('home-b2');
  }, [data.empty]);

  const center = globeCenter(data.regions);
  const containerHeight = heroHeight * CONTAINER_HEIGHT;
  const visibleRadius = Math.min(heroHeight * RADIUS_SHARE, RADIUS_MAX);
  // Arredondados, para pequenas mudanças de altura não recriarem o globo.
  const zoom =
    heroHeight > 0 ? Math.round(zoomForRadius(visibleRadius, containerHeight, center[1]) * 50) / 50 : 0;
  const diveOffsetY = Math.round(heroHeight * (DIVE_AT - CONTAINER_HEIGHT / 2));

  const points = useMemo(() => {
    const tr: Translate = (key, options) => t(key, options) as string;
    return regionPoints(
      data.regions,
      (r) => (r.grouped ? tr('home3b_region', { defaultValue: '{{name}} e região', name: r.name }) : r.name),
      (n) => countText(tr, 'lists', n, lang),
    );
  }, [data.regions, lang, t]);
  const detailPoints = useMemo(() => {
    const tr: Translate = (key, options) => t(key, options) as string;
    return cityPoints(data.cities, (n) => countText(tr, 'lists', n, lang));
  }, [data.cities, lang, t]);

  const tr: Translate = (key, options) => t(key, options) as string;
  const types = captureTypes(session);
  const captureModules = PRIMARY_CAPTURE_PRESENTATIONS.filter(({ module }) => types.includes(module));

  // Enquanto as listas chegam, linhas no lugar da frase (a altura da faixa não muda depois).
  let summary: JSX.Element | null = (
    <div className="v3b-summary-sk">
      <LoadingStatus />
      <Skeleton className="sk--dark" width="92%" height={14} />
      <Skeleton className="sk--dark" width="64%" height={14} />
    </div>
  );
  if (data.empty) {
    summary = (
      <p className={summaryClass}>
        {tr('home3b_empty', { defaultValue: 'Sua primeira lista aparece aqui, com as cidades no globo.' })}
      </p>
    );
  } else if (data.totalLists !== null) {
    const sentence = tr('home3b_summary', {
      defaultValue: 'Você já capturou <b>{{contacts}}</b> em {{lists}}.',
      contacts: countText(tr, 'contacts', data.totalContacts, lang),
      lists: countText(tr, 'lists', data.totalLists, lang),
    });
    const coverage = coverageText(tr, data.coverage, lang);
    summary = (
      <RichText as="p" className={summaryClass} html={coverage ? `${sentence} ${coverage}` : sentence} />
    );
  }

  return (
    <div className={`vx vx--hero v3b${data.empty ? ' v3b--b2' : ''}`}>
      <section className="vx-hero v3b-hero" ref={heroRef}>
        <div className="vx-hero__inner v3b-hero__inner">
          {data.empty ? (
            /* Início sem listas (B2, protótipo do guia passo a passo de 28/10/2026): os três tipos
               de captura e o convite para começar com o guia, no lugar do resumo e dos totais. */
            <div className="vx-hero__text v3b-b2">
              <h1>{gtx('b2_title')}</h1>
              <p>{gtx('b2_text')}</p>
              <div className="v3b-b2__tipos" role="group" aria-label={gtx('b2_types')}>
                {captureModules.map(({ module, icon }) => (
                  <button
                    type="button"
                    key={module}
                    className="v3b-b2__tipo"
                    onClick={() => openCaptureWindow(module)}
                  >
                    <span className="v3b-b2__icone material-symbols-rounded" aria-hidden="true">
                      {icon}
                    </span>
                    <strong>
                      {tr(`home3b_capture_${module}_title`, { defaultValue: CAPTURE_DEFAULTS[module].title })}
                    </strong>
                    <RichText as="span" html={gtx(`b2_${module}` as GuiaKey)} />
                    <span className="v3b-b2__seta" aria-hidden="true">
                      <span className="material-symbols-rounded">arrow_forward</span>
                    </span>
                  </button>
                ))}
              </div>
              <div className="v3b-b2__ou" aria-hidden="true">
                <span />
                {gtx('b2_or')}
                <span />
              </div>
              <section className="v3b-b2__guia" aria-labelledby="b2-guia">
                <span className="v3b-b2__guia-icone material-symbols-rounded" aria-hidden="true">
                  play_circle
                </span>
                <div>
                  <h2 id="b2-guia">{gtx('b2_help_title')}</h2>
                  <p>{gtx('b2_help_text')}</p>
                </div>
                <button
                  type="button"
                  className="v3b-b2__guia-btn"
                  onClick={() => {
                    const guia = useGuiaStore.getState();
                    if (guia.status !== 'ativo') comecarGuia('inicio');
                    else if (guia.passo === 'escolher') guia.abrirEscolha();
                    else guia.recolher(false);
                  }}
                >
                  <span className="material-symbols-rounded" aria-hidden="true">
                    play_circle
                  </span>
                  {guiaLigado ? gtx('menu_continue') : gtx('b2_help_go')}
                </button>
              </section>
              <LicensePill license={data.license} lang={lang} />
            </div>
          ) : (
            <div className="vx-hero__text">
            <h1>{tr('home3b_title', { defaultValue: 'Seu mapa de prospecção' })}</h1>
            {summary}
            <div className="vx-tiles">
              {HOME_MODULES.map((module) => (
                <div className="vx-tile" key={module}>
                  <span className="vx-icon vx-icon--sm vx-icon--square" aria-hidden="true">
                    <span className="material-symbols-rounded">{MODULE_ICON[module]}</span>
                  </span>
                  <TileValue value={data.totals[module]} locale={localeOf(lang)} count={countTotals} />
                  <span>{tr(`home3b_unit_${module}`, { defaultValue: UNIT_DEFAULTS[module] })}</span>
                </div>
              ))}
            </div>
            <LicensePill license={data.license} lang={lang} />
            </div>
          )}
        </div>

        <div className="v3b-globe" aria-hidden="true">
          {heroHeight > 0 ? (
            <Suspense fallback={null}>
              <GlobeMap
                points={points}
                detailPoints={detailPoints}
                center={center}
                zoom={zoom}
                labels="top3"
                coveredBottom={CARDS_OVERLAP}
                latOffset={LAT_OFFSET}
                diveOffsetY={diveOffsetY}
              />
            </Suspense>
          ) : null}
        </div>

      </section>

      {captureModules.length > 0 && !data.empty ? (
        <section
          className="v3b-capture"
          aria-label={tr('home3b_capture_aria', { defaultValue: 'Capturar leads' })}
        >
          {captureModules.map(({ module, icon }) => (
            <button
              type="button"
              className="vx-card vx-hero-option ds-elevavel"
              key={module}
              onClick={() => openCaptureWindow(module)}
            >
              {/* Ícone neutro no alto, como no menu do Capturar Leads (18/10/2026). */}
              <span className="vx-hero-option__icon material-symbols-rounded" aria-hidden="true">
                {icon}
              </span>
              <span className="vx-hero-option__body">
                <span className="vx-eyebrow">{tr('home3b_capture', { defaultValue: 'Capturar' })}</span>
                <span className="vx-capture-copy">
                  <strong>
                    {tr(`home3b_capture_${module}_title`, { defaultValue: CAPTURE_DEFAULTS[module].title })}
                  </strong>
                  <RichText
                    as="span"
                    html={tr(`home3b_capture_${module}_details`, {
                      defaultValue: CAPTURE_DEFAULTS[module].details,
                    })}
                  />
                </span>
              </span>
              {/* Cartão de entrada: só a seta avança 3 px no mouse ou no foco (M10 b). */}
              <span className="vx-round-arrow" aria-hidden="true">
                <span className="ds-seta-avanca">
                  <span className="material-symbols-rounded vx-symbol">arrow_forward</span>
                </span>
              </span>
            </button>
          ))}
        </section>
      ) : null}
    </div>
  );
}
